import {
  isCondition,
  type FieldDefinition,
  type Filter,
} from "@estelle/domain";
import { ChevronDown, ListFilter } from "lucide-react";
import { useState } from "react";

import { AddFilter } from "@/components/filters/AddFilter";
import { ConditionEditor } from "@/components/filters/ConditionEditor";
import { newConditionFor } from "@/components/filters/defaults";
import { MobileFilterSheet } from "@/components/filters/MobileFilterSheet";
import { addCondition } from "@/components/filters/tree";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

const chipClass =
  "inline-flex shrink-0 items-center gap-1 rounded-full border border-dashed px-3 text-sm whitespace-nowrap transition-colors hover:bg-accent focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none";

/**
 * The phone's one filter control (#647): a Filters button beside the search
 * box, with how many filters are on, opening the sheet with the list's common
 * filters at the top and every other field under More. It replaces the row of
 * chips that took a line of its own and ran off the screen's edge; the
 * filters in force still show as pills in the search box.
 */
export function MobileFiltersButton({
  fields,
  common,
  value,
  onChangeFilter,
  showLabel,
}: {
  fields: FieldDefinition[];
  common: FieldDefinition[];
  value: Filter;
  onChangeFilter: (next: Filter) => void;
  showLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const total = value.children.length;
  return (
    <>
      <button
        type="button"
        aria-label={total > 0 ? `Filters, ${String(total)} on` : "Filters"}
        onClick={() => {
          setOpen(true);
        }}
        className={cn(
          "relative inline-flex size-9 shrink-0 items-center justify-center rounded-md border transition-colors",
          "hover:bg-accent focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
          total > 0 && "border-primary bg-primary/10 text-primary-strong",
        )}
      >
        <ListFilter aria-hidden className="size-4" />
        {total > 0 ? (
          <span className="bg-primary text-primary-foreground absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold tabular-nums">
            {total}
          </span>
        ) : null}
      </button>
      <MobileFilterSheet
        open={open}
        onOpenChange={setOpen}
        fields={fields}
        common={common}
        value={value}
        onChange={onChangeFilter}
        showLabel={showLabel}
      />
    </>
  );
}

/**
 * The filters a list is usually narrowed by, as chips under its search box
 * (#633), with "+ Filter" for the rest.
 *
 * A chip for a field that is not in the filter yet: picking it opens that
 * field's value picker, and once applied the condition moves into the search
 * box as a solid pill with its ×, so a field is never offered twice. On a
 * phone the row scrolls sideways, every chip opens the bottom sheet on its own
 * field, and "All filters" opens the sheet's full list with these at the top.
 */
export function CommonFilters({
  fields,
  common,
  value,
  onChangeFilter,
  showLabel,
}: {
  fields: FieldDefinition[];
  common: FieldDefinition[];
  value: Filter;
  onChangeFilter: (next: Filter) => void;
  /** The phone sheet's button: "Show 78 orders". */
  showLabel?: string;
}) {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState<string | null>(null);
  const [sheet, setSheet] = useState<{ open: boolean; field?: string }>({
    open: false,
  });

  const applied = new Set(
    value.children.filter(isCondition).map((condition) => condition.field),
  );
  const offered = common.filter((field) => !applied.has(field.name));
  const total = value.children.length;

  if (isMobile) {
    return (
      <>
        <div className="-mx-3 flex min-w-0 gap-2 overflow-x-auto px-3 [scrollbar-width:none]">
          <button
            type="button"
            onClick={() => {
              setSheet({ open: true });
            }}
            className={cn(
              chipClass,
              "min-h-9 border-solid",
              total > 0 && "border-primary bg-primary/10 text-primary-strong",
            )}
          >
            <ListFilter aria-hidden className="size-3.5" />
            All filters{total > 0 ? ` (${String(total)})` : ""}
          </button>
          {offered.map((field) => (
            <button
              key={field.name}
              type="button"
              onClick={() => {
                setSheet({ open: true, field: field.name });
              }}
              className={cn(chipClass, "min-h-9")}
            >
              {field.label}
              <ChevronDown aria-hidden className="size-3.5 opacity-60" />
            </button>
          ))}
        </div>
        <MobileFilterSheet
          open={sheet.open}
          onOpenChange={(open) => {
            setSheet((current) => ({ ...current, open }));
          }}
          fields={fields}
          common={common}
          value={value}
          initialField={sheet.field}
          onChange={onChangeFilter}
          showLabel={showLabel}
        />
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <AddFilter
        fields={fields}
        common={common}
        value={value}
        onChangeFilter={onChangeFilter}
        className="h-7 rounded-full"
      />
      {offered.map((field) => (
        <Popover
          key={field.name}
          open={editing === field.name}
          onOpenChange={(open) => {
            setEditing(open ? field.name : null);
          }}
        >
          <PopoverTrigger asChild>
            <button type="button" className={cn(chipClass, "h-7")}>
              {field.label}
              <ChevronDown aria-hidden className="size-3.5 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-3">
            <ConditionEditor
              condition={newConditionFor(field)}
              fields={fields}
              submitLabel="Apply"
              onSubmit={(condition) => {
                onChangeFilter(addCondition(value, condition));
                setEditing(null);
              }}
              onRemove={() => {
                setEditing(null);
              }}
            />
          </PopoverContent>
        </Popover>
      ))}
    </div>
  );
}
